import { Particle } from '../ParticleLayer';

// Import Avatars
import AstridAvatar from '../../assets/custom/AstridAvatar.png';
import CorilAvatar from '../../assets/custom/CorilAvatar.png';
import ErokBuruAvatar from '../../assets/custom/Erok-BuruAvatar.png';
import EvelionAvatar from '../../assets/custom/EvelionAvatar.png';
import FerrimAvatar from '../../assets/custom/FerrimAvatar.png';
import GemataAvatar from '../../assets/custom/GemataAvatar.png';
import LorianAvatar from '../../assets/custom/LorianAvatar.png';
import NorimAvatar from '../../assets/custom/NorimAvatar.png';
import NyrixAvatar from '../../assets/custom/NyrixAvatar.png';
import ShanuAvatar from '../../assets/custom/ShanuAvatar.png';
import TerrikAvatar from '../../assets/custom/TerrikAvatar.png';
import VeriskAvatar from '../../assets/custom/VeriskAvatar.png';

const AVATAR_MAP: Record<string, string> = {
    'Astrid': AstridAvatar,
    'Coril': CorilAvatar,
    'Erok-Buru': ErokBuruAvatar,
    'Evelion': EvelionAvatar,
    'Ferrim': FerrimAvatar,
    'Gemata': GemataAvatar,
    'Lorian': LorianAvatar,
    'Norim': NorimAvatar,
    'Nyrix': NyrixAvatar,
    'Shanu': ShanuAvatar,
    'Terrik': TerrikAvatar,
    'Verisk': VeriskAvatar,
};

// Pre-load images
const loadedImages: Record<string, HTMLImageElement> = {};
Object.entries(AVATAR_MAP).forEach(([key, src]) => {
    const img = new Image();
    img.src = src;
    loadedImages[key] = img;
});

// GLOBAL SPRITE CACHE FOR ENERGIZE EFFECT
const energizeSpriteCache = new Map<string, HTMLCanvasElement>();

function getCloneGlowSprite(size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `clone-glow-${qSize}`;
    let sprite = energizeSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = qSize;
        sprite.height = qSize;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = qSize / 2;
            const centerY = qSize / 2;

            // 1. Overlay "Screen" blend for glowing look
            sCtx.globalCompositeOperation = 'source-over'; // Default but good to be explicit for base
            sCtx.fillStyle = 'rgba(253, 224, 71, 0.6)'; // Strong yellow tint
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize / 2, 0, Math.PI * 2);
            sCtx.fill();

            // 2. Add extra shine
            sCtx.globalCompositeOperation = 'lighter';
            sCtx.fillStyle = 'rgba(255, 255, 255, 0.4)';
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize / 3, 0, Math.PI * 2);
            sCtx.fill();
        }
        energizeSpriteCache.set(key, sprite);
    }
    return sprite;
}

export type EnergizeEffectData = {
    targetId: string;
    centerX: number;
    centerY: number;

    // Clones
    clones: {
        id: number;
        x: number;
        y: number;
        angle: number; // For swirl phase
        radius: number; // For swirl phase
        phase: 'APPROACH' | 'SWIRL' | 'ILLUSION_SPREAD' | 'ILLUSION_DASH';
        opacity: number;
        scale: number;
        targetX?: number;
    }[];
    hasSpawnedIllusions: boolean;

    // Lightning Traces
    traces: {
        points: { x: number, y: number }[];
        life: number;
        maxLife: number;
        color: string;
        width: number;
    }[];
};

export type EnergizeEffect = {
    kind: 'energize';
    t: number;
    data: EnergizeEffectData;
    x?: number;
    y?: number;
};

export function createEnergizeEffect(
    centerX: number,
    centerY: number,
    targetId: string = 'Astrid'
): EnergizeEffect {
    return {
        kind: 'energize',
        t: 0,
        x: centerX,
        y: centerY,
        data: {
            targetId,
            centerX,
            centerY,
            clones: [
                // Start far left
                {
                    id: 1,
                    x: centerX - 500,
                    y: centerY,
                    angle: Math.PI,
                    radius: 500,
                    phase: 'APPROACH',
                    opacity: 0,
                    scale: 1
                },
                // Start far right
                {
                    id: 2,
                    x: centerX + 500,
                    y: centerY,
                    angle: 0,
                    radius: 500,
                    phase: 'APPROACH',
                    opacity: 0,
                    scale: 1
                }
            ],
            traces: [],
            hasSpawnedIllusions: false
        },
    };
}

function generateLightningPoints(x1: number, y1: number, x2: number, y2: number, displace: number): { x: number, y: number }[] {
    if (displace < 2) {
        return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
    }
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    const dx = (x2 - x1);
    const dy = (y2 - y1);
    const normalX = -dy;
    const normalY = dx;
    const len = Math.sqrt(dx * dx + dy * dy);
    const normalizedX = normalX / len;
    const normalizedY = normalY / len;

    const offset = (Math.random() - 0.5) * displace;
    const disX = midX + normalizedX * offset;
    const disY = midY + normalizedY * offset;

    const left = generateLightningPoints(x1, y1, disX, disY, displace / 2);
    const right = generateLightningPoints(disX, disY, x2, y2, displace / 2);

    return [...left, ...right.slice(1)];
}

export function renderEnergizeEffect(
    effect: EnergizeEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean {
    const TOTAL_TIME = 5500;
    const df = (effect as any)._df ?? 1;
    const { data, t } = effect;

    if (t > TOTAL_TIME) return false;

    const img = loadedImages[data.targetId] || loadedImages['Astrid'];

    // --- Illusion Phase Trigger ---
    if (t > 3200 && !data.hasSpawnedIllusions) {
        data.hasSpawnedIllusions = true;
        // Clear existing clones
        data.clones = [];

        // Spawn 5 clones at center
        const offsets = [0, -200, 200, -400, 400];
        offsets.forEach((offset, i) => {
            data.clones.push({
                id: i + 10,
                x: data.centerX,
                y: data.centerY,
                angle: 0,
                radius: 0,
                phase: 'ILLUSION_SPREAD',
                opacity: 1,
                scale: i === 0 ? 1.2 : 1, // Center one slightly bigger
                targetX: data.centerX + offset
            });
        });
    }

    // --- Update Clones ---
    data.clones.forEach((clone, index) => {
        const prevX = clone.x;
        const prevY = clone.y;

        // Fade in/out
        if (t < 300) clone.opacity = t / 300;
        else if (t > TOTAL_TIME - 500) clone.opacity = (TOTAL_TIME - t) / 500;
        else clone.opacity = 1;

        if (clone.phase === 'APPROACH') {
            const targetRadius = 60;
            const dist = Math.abs(clone.x - data.centerX);

            if (dist <= targetRadius + 5) {
                clone.phase = 'SWIRL';
                clone.radius = targetRadius;
                clone.angle = clone.x < data.centerX ? Math.PI : 0;
            } else {
                const speed = (15 + (dist / 500) * 10) * df;
                if (clone.x < data.centerX) clone.x += speed;
                else clone.x -= speed;

                clone.y = data.centerY + Math.sin(t * 0.01 + index) * 20;
            }
        } else if (clone.phase === 'SWIRL') {
            const swirlTime = t - 1000;
            const speed = (0.1 + (swirlTime / 2000) * 0.3) * df;

            clone.angle += speed;
            clone.radius = 60 + Math.sin(t * 0.005) * 10;
            if (t > 3000) clone.radius *= Math.pow(0.9, df);

            clone.x = data.centerX + Math.cos(clone.angle) * clone.radius;
            clone.y = data.centerY + Math.sin(clone.angle) * clone.radius;
        } else if (clone.phase === 'ILLUSION_SPREAD') {
            if (clone.targetX !== undefined) {
                const dx = clone.targetX - clone.x;
                clone.x += dx * 0.2 * df;

                if (Math.abs(dx) < 5 && t > 4000) {
                    clone.phase = 'ILLUSION_DASH';
                    const currentOffset = clone.targetX - data.centerX;
                    clone.targetX = data.centerX - currentOffset;
                }
            }
        } else if (clone.phase === 'ILLUSION_DASH') {
            if (clone.targetX !== undefined) {
                const dx = clone.targetX - clone.x;
                const speed = 60 * df;
                if (Math.abs(dx) < speed) {
                    clone.x = clone.targetX;
                } else {
                    clone.x += Math.sign(dx) * speed;
                }

                clone.y = data.centerY + (Math.random() - 0.5) * 10;
            }
        }

        // Spawn Lightning Trace
        // More frequent traces for smoother look
        // For Illusion Dash, we want even more traces
        const traceFrequency = (clone.phase === 'ILLUSION_DASH' || clone.phase === 'ILLUSION_SPREAD') ? 8 : 16;

        if (t % 32 < traceFrequency) {
            const points = generateLightningPoints(prevX, prevY, clone.x, clone.y, 15);
            data.traces.push({
                points,
                life: 500,
                maxLife: 500,
                color: '#FDE047', // Yellow-400
                width: 4
            });
        }

        // Draw Clone
        if (img && clone.opacity > 0.01) {
            ctx.save();
            ctx.translate(clone.x, clone.y);
            ctx.globalAlpha = clone.opacity;

            // Strong Yellow Glow
            ctx.shadowColor = '#FEF08A'; // Yellow-200
            ctx.shadowBlur = 30;

            const size = 90 * clone.scale;

            // 1. Draw base image
            ctx.drawImage(img, -size / 2, -size / 2, size, size);

            // 2 & 3. Drawn via pre-rendered sprite
            ctx.globalCompositeOperation = 'screen'; // Use screen for the glow sprite over the image
            const glowSprite = getCloneGlowSprite(size);
            ctx.drawImage(glowSprite, -size / 2, -size / 2);

            ctx.restore();
        }
    });

    // --- Render Traces ---
    for (let i = data.traces.length - 1; i >= 0; i--) {
        const trace = data.traces[i];
        trace.life -= 16 * df;
        if (trace.life <= 0) {
            data.traces[i] = data.traces[data.traces.length - 1];
            data.traces.pop();
            continue;
        }

        const lifeRatio = trace.life / trace.maxLife;

        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Outer Glow (Wider, more transparent)
        ctx.globalAlpha = lifeRatio * 0.4;
        ctx.strokeStyle = '#FEF9C3'; // Yellow-100
        ctx.lineWidth = trace.width * 3;
        ctx.shadowColor = '#FDE047';
        ctx.shadowBlur = 15;

        ctx.beginPath();
        if (trace.points.length > 0) {
            ctx.moveTo(trace.points[0].x, trace.points[0].y);
            for (let j = 1; j < trace.points.length; j++) {
                ctx.lineTo(trace.points[j].x, trace.points[j].y);
            }
        }
        ctx.stroke();

        // Inner Core (Thinner, brighter)
        ctx.globalAlpha = lifeRatio;
        ctx.strokeStyle = '#FFFFFF'; // White core
        ctx.lineWidth = trace.width;
        ctx.shadowBlur = 5;

        ctx.stroke();
        ctx.restore();
    }

    return true;
}
